if (!customElements.get('pdp-cross-sell-o2')) {
  customElements.define(
    'pdp-cross-sell-o2',
    class PdpCrossSellO2 extends HTMLElement {
      constructor() {
        super();
        this.list = this.querySelector('.cross-sell-o2__list');
        this.toggleButton = this.querySelector('[data-cross-sell-toggle]');
        this.toggleMoreLabel = this.querySelector('[data-cross-sell-toggle-more]');
        this.toggleLessLabel = this.querySelector('[data-cross-sell-toggle-less]');
        this.isAnimating = false;
        this.onToggleClick = this.handleToggleClick.bind(this);
      }

      connectedCallback() {
        if (this.toggleButton) {
          this.toggleButton.addEventListener('click', this.onToggleClick);
          this.syncToggleState(false);
        }
      }

      disconnectedCallback() {
        if (this.toggleButton) {
          this.toggleButton.removeEventListener('click', this.onToggleClick);
        }
      }

      handleToggleClick() {
        if (this.isAnimating) return;
        const expanded = this.classList.contains('is-expanded');
        this.syncToggleState(!expanded, { animate: true });
      }

      syncToggleState(expanded, { animate = false } = {}) {
        if (!animate || this.prefersReducedMotion() || !this.list) {
          this.finishToggleState(expanded);
          return;
        }

        this.updateToggleLabels(expanded);
        this.animateListHeight(expanded);
      }

      prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      updateToggleLabels(expanded) {
        if (this.toggleButton) {
          this.toggleButton.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        }
        if (this.toggleMoreLabel) this.toggleMoreLabel.hidden = expanded;
        if (this.toggleLessLabel) this.toggleLessLabel.hidden = !expanded;
      }

      finishToggleState(expanded) {
        this.classList.toggle('is-expanded', expanded);
        this.classList.remove('is-collapsing');
        this.cleanupListAnimationStyles();
        this.updateToggleLabels(expanded);
      }

      animateListHeight(expanded) {
        const isExpanded = this.classList.contains('is-expanded');
        if (expanded === isExpanded) {
          this.finishToggleState(expanded);
          return;
        }

        this.isAnimating = true;

        const startHeight = this.list.scrollHeight;
        let endHeight = startHeight;

        this.list.style.height = `${startHeight}px`;
        this.list.style.overflow = 'hidden';
        this.list.style.willChange = 'height';
        this.list.style.transition = 'none';

        if (expanded) {
          this.classList.add('is-expanded');
          endHeight = this.list.scrollHeight;
          this.classList.remove('is-expanded');
        } else {
          // Measure collapsed height, then restore expanded state before animation starts.
          // This prevents a visual snap where rows 3-5 disappear before the tween runs.
          this.classList.remove('is-expanded');
          endHeight = this.list.scrollHeight;
          this.classList.add('is-expanded');

          // Keep collapsed rows rendered while animating the list height down.
          this.classList.add('is-collapsing');
        }

        if (Math.abs(startHeight - endHeight) < 1) {
          this.finishToggleState(expanded);
          this.isAnimating = false;
          return;
        }

        requestAnimationFrame(() => {
          if (expanded) {
            this.classList.add('is-expanded');
          } else {
            this.classList.remove('is-expanded');
          }

          // Force layout so transition starts from the locked start height.
          void this.list.offsetHeight;

          requestAnimationFrame(() => {
            this.list.style.transition = 'height 220ms ease-out';
            this.list.style.height = `${endHeight}px`;
          });
        });

        const onAnimationEnd = (event) => {
          if (event.target !== this.list || event.propertyName !== 'height') return;
          this.list.removeEventListener('transitionend', onAnimationEnd);
          this.finishToggleState(expanded);
          this.isAnimating = false;
        };

        this.list.addEventListener('transitionend', onAnimationEnd);
      }

      cleanupListAnimationStyles() {
        this.list.style.height = '';
        this.list.style.overflow = '';
        this.list.style.willChange = '';
        this.list.style.transition = '';
      }
    }
  );
}

if (!customElements.get('cart-cross-sell-o2')) {
  customElements.define(
    'cart-cross-sell-o2',
    class CartCrossSellO2 extends HTMLElement {
      constructor() {
        super();
        this.feedbackElement = this.querySelector('[data-cross-sell-feedback]');
        this.onClick = this.handleClick.bind(this);
      }

      connectedCallback() {
        this.addEventListener('click', this.onClick);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
      }

      async handleClick(event) {
        if (!(event.target instanceof Element)) return;

        const addButton = event.target.closest('[data-cross-sell-add]');
        if (!addButton || addButton.disabled) return;

        const variantId = Number(addButton.dataset.variantId || 0);
        if (!Number.isFinite(variantId) || variantId <= 0 || !Number.isInteger(variantId)) {
          const message = this.dataset.addInvalidVariantMessage || this.dataset.addErrorMessage || '';
          this.showAddError(addButton, message);
          console.warn('[cart-cross-sell-o2] Missing or invalid data-variant-id on add button.', addButton);
          return;
        }

        const priceAtAdd = addButton.dataset.priceAtAdd;
        if (!o2IsValidPriceAtAdd(priceAtAdd)) {
          const message = this.dataset.addErrorMessage || '';
          this.showAddError(addButton, message);
          console.warn('[cart-cross-sell-o2] Missing or invalid data-price-at-add on add button.', addButton);
          return;
        }

        this.setFeedback('');
        this.setCardFeedback(addButton, '');
        this.setButtonLoading(addButton, true);

        let cartStateForSync = null;
        let cartUpdateUnsubscriber;

        try {
          const sectionsToRender = this.getSectionsToRender();
          if (!sectionsToRender.length) {
            throw new Error('Unable to refresh cart UI because required sections are missing on this page.');
          }

          const requestConfig = fetchConfig('javascript');
          requestConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
          requestConfig.headers['Content-Type'] = 'application/json';
          requestConfig.body = JSON.stringify({
            items: [o2BuildCrossSellLineItem(variantId, priceAtAdd)],
            sections: sectionsToRender.map((section) => section.section),
            sections_url: window.location.pathname,
          });

          const response = await fetch(routes.cart_add_url, requestConfig);

          let responseJson;
          try {
            responseJson = await response.json();
          } catch (parseError) {
            console.warn('[cart-cross-sell-o2] Cart add response was not valid JSON.', {
              status: response.status,
              statusText: response.statusText,
              contentType: response.headers.get('content-type'),
              cause: parseError,
            });
            if (!response.ok) {
              throw new Error(`Cart add failed (${response.status}).`);
            }
            throw new Error('Cart add succeeded but the response could not be read.');
          }

          if (!response.ok || responseJson.status) {
            throw new Error(this.resolveCartAddErrorMessage(responseJson, this.dataset.addErrorMessage || ''));
          }

          const cartUpdate = {
            source: 'cart-cross-sell-o2',
            productVariantId: variantId,
            cartData: responseJson,
          };
          let cartChangedAfterAdd = false;
          cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (update) => {
            if (update !== cartUpdate) cartChangedAfterAdd = true;
          });
          const cartUpdatePromise = publish(PUB_SUB_EVENTS.cartUpdate, cartUpdate).catch(console.error);

          let cartState = responseJson;
          const discountCode = this.normalizeDiscountCode(addButton.dataset.crossSellDiscountCode);
          if (discountCode) {
            try {
              const discountState = await this.appendDiscountCodes([discountCode], {
                sections: sectionsToRender.map((section) => section.section),
              });
              if (discountState) cartState = discountState;
            } catch (discountError) {
              console.error(discountError);
              const message = this.dataset.addErrorMessage || '';
              this.showAddError(addButton, message);
            }
          }

          do {
            cartChangedAfterAdd = false;
            cartState = await this.fetchFreshCartState(sectionsToRender);
            await cartUpdatePromise;
          } while (cartChangedAfterAdd);
          cartStateForSync = cartState;

          if (this.dataset.crossSellContext === 'cart-drawer') {
            const cartDrawer = document.querySelector('cart-drawer');
            if (!cartDrawer || typeof cartDrawer.renderContents !== 'function') {
              throw new Error('Cart drawer is not available for re-render.');
            }
            cartDrawer.renderContents(cartState);
          } else {
            this.refreshCartPageSections(sectionsToRender, cartState.sections || {});
          }
        } catch (error) {
          console.error(error);
          const message = error?.message || this.dataset.addErrorMessage || '';
          this.showAddError(addButton, message);
        } finally {
          cartUpdateUnsubscriber?.();
          this.setButtonLoading(addButton, false);
          if (cartStateForSync) window.O2CartOrderLimit?.syncFromCartData?.(cartStateForSync);
          else await window.O2CartOrderLimit?.syncCartState?.();
        }
      }

      async fetchFreshCartState(sectionsToRender) {
        const sectionIds = sectionsToRender.map((section) => section.section).filter(Boolean);
        const cartResponsePromise = fetch(`${routes.cart_url}.js`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        const sectionResponsePromises = sectionIds.map(async (sectionId) => {
          const sectionUrl = new URL(routes.cart_url, window.location.origin);
          sectionUrl.searchParams.set('section_id', sectionId);
          const response = await fetch(sectionUrl, { credentials: 'same-origin' });
          if (!response.ok) throw new Error(this.dataset.addErrorMessage || 'Unable to refresh the cart.');
          return [sectionId, await response.text()];
        });

        const [cartResponse, sectionEntries] = await Promise.all([
          cartResponsePromise,
          Promise.all(sectionResponsePromises),
        ]);

        if (!cartResponse.ok) {
          throw new Error(this.dataset.addErrorMessage || 'Unable to refresh the cart.');
        }

        const cartState = await cartResponse.json();
        return { ...cartState, sections: Object.fromEntries(sectionEntries) };
      }

      resolveCartAddErrorMessage(responseJson, fallbackMessage = '') {
        const description = typeof responseJson?.description === 'string' ? responseJson.description.trim() : '';
        if (description) return description;

        const message = typeof responseJson?.message === 'string' ? responseJson.message.trim() : '';
        if (message) return message;

        const errors = responseJson?.errors;
        if (typeof errors === 'string' && errors.trim()) return errors.trim();

        if (errors && typeof errors === 'object') {
          const errorValues = Object.values(errors)
            .map((value) => {
              if (Array.isArray(value)) return value.join(' ');
              if (typeof value === 'string') return value;
              return '';
            })
            .map((value) => value.trim())
            .filter(Boolean);

          if (errorValues.length) return errorValues[0];
        }

        return fallbackMessage;
      }

      refreshCartPageSections(sectionsToRender, sectionsMarkup) {
        sectionsToRender.forEach((section) => {
          const targetRoot = document.getElementById(section.id);
          if (!targetRoot || !sectionsMarkup[section.section]) return;

          const targetElement = section.selector
            ? targetRoot.querySelector(section.selector) || targetRoot
            : targetRoot;
          const sourceMarkup = this.getSectionInnerHTML(sectionsMarkup[section.section], section.selector);
          if (sourceMarkup === null) return;
          targetElement.innerHTML = sourceMarkup;
        });
      }

      getSectionInnerHTML(html, selector = '.shopify-section') {
        const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
        const sourceElement = parsedDocument.querySelector(selector);
        if (!sourceElement) return null;
        return sourceElement.innerHTML;
      }

      getSectionsToRender() {
        if (this.dataset.crossSellContext === 'cart-drawer') {
          return [
            {
              id: 'CartDrawer',
              section: 'cart-drawer',
              selector: '.drawer__inner',
            },
            {
              id: 'cart-icon-bubble',
              section: 'cart-icon-bubble',
              selector: '.shopify-section',
            },
          ];
        }

        const sections = [
          {
            id: 'main-cart-items',
            section: document.getElementById('main-cart-items')?.dataset.id,
            selector: '.js-contents',
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
          {
            id: 'cart-live-region-text',
            section: 'cart-live-region-text',
            selector: '.shopify-section',
          },
          {
            id: 'main-cart-footer',
            section: document.getElementById('main-cart-footer')?.dataset.id,
            selector: '.js-contents',
          },
        ];

        return sections.filter((section) => section.section);
      }

      setFeedback(message) {
        if (!this.feedbackElement) return;

        if (!message) {
          this.feedbackElement.hidden = true;
          this.feedbackElement.textContent = '';
          return;
        }

        this.feedbackElement.textContent = message;
        this.feedbackElement.hidden = false;
      }

      getCardErrorElements(triggerElement) {
        if (!(triggerElement instanceof Element)) return null;

        const productCard = triggerElement.closest('product-card-o2');
        if (!productCard) return null;

        const errorWrapper = productCard.querySelector('[data-o2-card-add-error]');
        const errorText = productCard.querySelector('[data-o2-card-add-error-text]');
        if (!(errorWrapper instanceof HTMLElement) || !(errorText instanceof HTMLElement)) return null;

        return { errorWrapper, errorText };
      }

      showAddError(triggerElement, message) {
        if (window.O2CartOrderLimit?.showCartError) {
          window.O2CartOrderLimit.showCartError(message, this);
        } else {
          this.setCardFeedback(triggerElement, message);
        }
      }

      setCardFeedback(triggerElement, message) {
        const errorElements = this.getCardErrorElements(triggerElement);
        if (!errorElements) {
          this.setFeedback(message);
          return;
        }

        if (!message) {
          errorElements.errorText.textContent = '';
          errorElements.errorWrapper.hidden = true;
          this.setFeedback('');
          return;
        }

        errorElements.errorText.textContent = message;
        errorElements.errorWrapper.hidden = false;
        this.setFeedback('');
      }

      normalizeDiscountCode(code) {
        return String(code || '').trim();
      }

      getUniqueDiscountCodes(codes) {
        const seen = new Set();
        return codes
          .map((code) => this.normalizeDiscountCode(code))
          .filter((code) => {
            const key = code.toLowerCase();
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
          });
      }

      getDiscountCodesFromCartState(cartState) {
        const codes = [];
        const discountCodes = Array.isArray(cartState?.discount_codes) ? cartState.discount_codes : [];
        discountCodes.forEach((discountCode) => {
          if (typeof discountCode === 'string') {
            codes.push(discountCode);
          } else {
            codes.push(discountCode?.code);
          }
        });

        const cartLevelDiscounts = Array.isArray(cartState?.cart_level_discount_applications)
          ? cartState.cart_level_discount_applications
          : [];
        cartLevelDiscounts.forEach((discount) => {
          if (discount?.type === 'discount_code') codes.push(discount.title);
        });

        const items = Array.isArray(cartState?.items) ? cartState.items : [];
        items.forEach((item) => {
          const allocations = Array.isArray(item?.line_level_discount_allocations)
            ? item.line_level_discount_allocations
            : [];
          allocations.forEach((allocation) => {
            const application = allocation?.discount_application;
            if (application?.type === 'discount_code') codes.push(application.title);
          });
        });

        return this.getUniqueDiscountCodes(codes);
      }

      async fetchCartDiscountCodes() {
        const response = await fetch(`${routes.cart_url}.js`, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error(`Unable to load current cart discounts (${response.status}).`);
        const cartState = await response.json();
        return this.getDiscountCodesFromCartState(cartState);
      }

      async appendDiscountCodes(codes, { sections = [] } = {}) {
        const nextCodes = this.getUniqueDiscountCodes(codes);
        if (!nextCodes.length) return null;

        const existingCodes = await this.fetchCartDiscountCodes();
        const existingKeys = new Set(existingCodes.map((code) => code.toLowerCase()));
        const mergedCodes = [...existingCodes];
        nextCodes.forEach((code) => {
          const key = code.toLowerCase();
          if (!existingKeys.has(key)) {
            existingKeys.add(key);
            mergedCodes.push(code);
          }
        });

        if (mergedCodes.length === existingCodes.length) return null;

        const body = {
          discount: mergedCodes.join(','),
        };
        if (sections.length) {
          body.sections = sections;
          body.sections_url = window.location.pathname;
        }

        const response = await fetch(`${routes.cart_update_url}`, {
          ...fetchConfig(),
          body: JSON.stringify(body),
        });
        if (!response.ok) throw new Error(`Unable to update cart discounts (${response.status}).`);
        return response.json();
      }

      setButtonLoading(button, isLoading) {
        if (!button) return;

        button.disabled = isLoading;
        button.setAttribute('aria-busy', isLoading ? 'true' : 'false');

        const labelElement = button.querySelector('[data-cross-sell-add-label]');
        if (!labelElement) return;

        labelElement.textContent = isLoading
          ? button.dataset.labelLoading || labelElement.textContent
          : button.dataset.labelDefault || labelElement.textContent;
      }
    }
  );
}
